/**
 * BEPReadinessPanel — pre-extraction BEP readiness audit (advisory).
 *
 * Data comes ONLY from:
 *   POST /api/projects/{id}/bep/readiness-audit          (start / stored)
 *   GET  /api/projects/{id}/bep/readiness-audit/status   (poll while running)
 *   GET  /api/projects/{id}/bep/readiness-audit/latest   (full result)
 * Every displayed number is read from these responses; absent fields
 * render an em dash. No fallback numbers, no timers/interpolation.
 *
 * Styling copied from:
 *   - components/PropertyCoveragePanel.js  (card-elevated wrapper,
 *     muted expandable table rows, text-xs tables)
 *   - pages/ProjectDetailPage.js Step 1 card (Card/CardHeader/CardTitle/
 *     CardDescription + Button conventions, title= tooltips)
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  ChevronDown, ChevronRight, Loader2, ClipboardCheck, AlertCircle,
  CheckCircle2, ArrowRight, RefreshCw, Upload,
} from 'lucide-react';
import { apiFetch } from '../lib/supabase';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DASH = '\u2014';

export const STEPS = [
  'Uploaded/registered',
  'SIR loaded',
  'Criteria evaluated',
  'Requirements classified',
  'Saved',
  'Complete',
];

// Backend stage boundaries (progress_percent is backend-written only).
export function stepStates(progress, failed) {
  const p = typeof progress === 'number' ? progress : 0;
  const done = [true, p >= 12, p >= 54, p >= 92, p >= 100, p >= 100];
  return STEPS.map((label, i) => {
    let state = 'pending';
    if (done[i]) state = 'complete';
    else if (i === 0 || done[i - 1]) state = failed ? 'failed' : 'processing';
    return { label, state };
  });
}

export const STEP_DOT = {
  pending: 'bg-muted-foreground/30',
  processing: 'bg-blue-500 animate-pulse',
  complete: 'bg-[hsl(var(--success,142_71%_45%))] bg-green-500',
  review: 'bg-amber-500',
  failed: 'bg-[hsl(var(--destructive))]',
};

export const SCORE_CHIP = {
  0: 'bg-[hsl(var(--destructive))]/10 text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]/30',
  1: 'bg-amber-500/10 text-amber-600 border-amber-500/30',
  2: 'bg-blue-500/10 text-blue-600 border-blue-500/30',
  3: 'bg-green-500/10 text-green-600 border-green-500/30',
};

export function num(v) {
  return v === null || v === undefined ? DASH : v;
}

export { DASH };

/**
 * Semicircular readiness gauge — plain SVG, no chart lib. Color follows
 * the approved score bands. Score/band come straight from the audit API.
 */
export function ReadinessGauge({ score, band, compact = false, testId }) {
  const val = (score === null || score === undefined) ? null : Number(score);
  const pct = val === null ? 0 : Math.max(0, Math.min(100, val));
  const color = val === null
    ? 'hsl(var(--muted-foreground))'
    : pct >= 80 ? '#15803d' : pct >= 60 ? '#b45309' : '#b91c1c';
  const ARC = Math.PI * 80;
  const width = compact ? 110 : 170;
  const fmt = val === null ? DASH
    : `${Number.isInteger(val) ? val : val.toFixed(1)}%`;
  return (
    <div className="flex flex-col items-center" style={{ width }}>
      <svg viewBox="0 0 200 118" style={{ width }} role="img" aria-label="BEP readiness score">
        <path d="M 20 100 A 80 80 0 0 1 180 100" fill="none"
          stroke="hsl(var(--muted))" strokeWidth="14" strokeLinecap="round" />
        {val !== null && (
          <path d="M 20 100 A 80 80 0 0 1 180 100" fill="none"
            stroke={color} strokeWidth="14" strokeLinecap="round"
            strokeDasharray={`${(pct / 100) * ARC} ${ARC}`} />
        )}
        <text x="100" y="95" textAnchor="middle" className="fill-foreground"
          style={{ font: `700 ${compact ? '38px' : '34px'} ui-sans-serif, system-ui` }}
          data-testid={testId}>
          {fmt}
        </text>
      </svg>
      {!compact && band && (
        <p className="text-[11px] text-center leading-tight" style={{ color }}>{band}</p>
      )}
    </div>
  );
}

export function MetricCard({ title, main, sub, testId }) {
  return (
    <div className="card-elevated p-3 flex flex-col gap-1" data-testid={testId}>
      <p className="text-[11px] text-muted-foreground">{title}</p>
      <p className="text-xl font-semibold tabular-nums leading-none">{main}</p>
      {sub && <p className="text-[11px] text-muted-foreground">{sub}</p>}
    </div>
  );
}

export function ChecklistRow({ c }) {
  const [open, setOpen] = useState(false);
  const score = c.score === null || c.score === undefined ? null : c.score;
  const chipCls = score === null
    ? 'bg-muted text-muted-foreground border-border'
    : SCORE_CHIP[score] || 'bg-muted text-muted-foreground border-border';
  return (
    <>
      <tr
        className="border-b border-border/50 hover:bg-muted/30 cursor-pointer"
        onClick={() => setOpen(!open)}
        data-testid={`readiness-criterion-${c.criterion_id}`}
      >
        <td className="py-1.5 pr-2 whitespace-nowrap">
          <span className="inline-flex items-center gap-1 font-medium">
            {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
            {c.criterion_id}
          </span>
        </td>
        <td className="py-1.5 pr-2">{c.name}
          {c.critical && (
            <Badge variant="outline"
              className="ml-2 text-[10px] px-1 py-0 border-[hsl(var(--destructive))]/40 text-[hsl(var(--destructive))]"
              data-testid={`readiness-critical-badge-${c.criterion_id}`}>
              CRITICAL
            </Badge>
          )}
        </td>
        <td className="py-1.5 pr-2">
          <span className={`inline-block border rounded px-1.5 text-[11px] tabular-nums ${chipCls}`}>
            {score === null ? 'N/A' : score}
          </span>
        </td>
        <td className="py-1.5 pr-2 text-muted-foreground">{c.status || DASH}</td>
      </tr>
      {open && (
        <tr className="bg-muted/20" data-testid={`readiness-criterion-detail-${c.criterion_id}`}>
          <td colSpan={4} className="py-2 pl-5 pr-3 text-[11px] space-y-2">
            {(c.evidence || []).length > 0 && (
              <div>
                <p className="font-semibold mb-0.5">Evidence</p>
                {c.evidence.map((e, i) => (
                  <p key={i} className="text-muted-foreground">
                    p.{num(e.page)} · <span className="font-mono">{e.block_id || DASH}</span> — “{e.text || DASH}”
                  </p>
                ))}
              </div>
            )}
            {(c.gaps || []).length > 0 && (
              <div>
                <p className="font-semibold mb-0.5">Gaps</p>
                {c.gaps.map((g, i) => <p key={i} className="text-muted-foreground">• {g}</p>)}
              </div>
            )}
            {(c.remediation || []).length > 0 && (
              <div>
                <p className="font-semibold mb-0.5">Remediation</p>
                {c.remediation.map((r, i) => <p key={i} className="text-muted-foreground">• {r}</p>)}
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

function BlockedGroup({ heading, families, testId }) {
  if (!families.length) return null;
  return (
    <div data-testid={testId}>
      <p className="text-xs font-semibold mb-1">{heading} ({families.length})</p>
      <ul className="space-y-0.5">
        {families.map((f) => (
          <li key={f.family_id} className="text-[11px] text-muted-foreground">
            <span className="font-medium text-foreground">{f.name || DASH}</span>
            {f.property_name ? ` · ${f.property_name}` : ''}
            {(f.missing_fields || []).length > 0 && ` — missing: ${f.missing_fields.join(', ')}`}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function BEPReadinessPanel({ projectId, hasBep = true, localBepFile = null, readOnly = false }) {
  const navigate = useNavigate();
  // phase: loading | none | running | failed | complete
  const [phase, setPhase] = useState('loading');
  const [statusRow, setStatusRow] = useState(null);
  const [result, setResult] = useState(null);
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState('');
  const [collapsed, setCollapsed] = useState(true); // default collapsed; not persisted
  const [reuploading, setReuploading] = useState(false);
  const [reuploadMsg, setReuploadMsg] = useState('');
  const [reuploadError, setReuploadError] = useState('');
  const fileInputRef = useRef(null);
  const pollRef = useRef(null);

  const stopPoll = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };

  const fetchLatest = useCallback(async () => {
    try {
      const resp = await apiFetch(`${API_URL}/api/projects/${projectId}/bep/readiness-audit/latest`);
      if (resp.status === 404) { setPhase('none'); return; }
      if (!resp.ok) { setPhase('none'); return; }
      const row = await resp.json();
      setStatusRow(row);
      if (row.status === 'completed') { setResult(row); setPhase('complete'); }
      else if (row.status === 'failed' || row.status === 'aborted') setPhase('failed');
      else setPhase('running');
    } catch { setPhase('none'); }
  }, [projectId]);

  const pollStatus = useCallback(() => {
    stopPoll();
    pollRef.current = setInterval(async () => {
      try {
        const resp = await apiFetch(`${API_URL}/api/projects/${projectId}/bep/readiness-audit/status`);
        if (!resp.ok) return;
        const s = await resp.json();
        setStatusRow((prev) => ({ ...prev, ...s }));
        if (s.status === 'completed') { stopPoll(); fetchLatest(); }
        else if (s.status === 'failed' || s.status === 'aborted') { stopPoll(); setPhase('failed'); }
      } catch { /* keep polling */ }
    }, 3000);
  }, [projectId, fetchLatest]);

  useEffect(() => { fetchLatest(); return stopPoll; }, [fetchLatest]);
  useEffect(() => { if (phase === 'running') { pollStatus(); setCollapsed(false); } }, [phase, pollStatus]);
  useEffect(() => { if (phase === 'failed') { setCollapsed(false); } }, [phase]);

  const startAudit = async () => {
    setStarting(true);
    setStartError('');
    try {
      // Pre-extraction path: no BEP on the server yet, but the user
      // dropped one in Step 1 — send it so the audit runs directly on it.
      let opts = { method: 'POST' };
      if (!hasBep && localBepFile) {
        const form = new FormData();
        form.append('bep_file', localBepFile);
        opts = { method: 'POST', body: form };
      }
      const resp = await apiFetch(`${API_URL}/api/projects/${projectId}/bep/readiness-audit`, opts);
      if (!resp.ok) {
        // Surface the backend detail verbatim — silent-catch here was
        // hiding "No BEP uploaded for this project" and every other
        // start-POST failure behind an unchanging UI.
        let detail = '';
        try {
          const err = await resp.json();
          detail = err?.detail || JSON.stringify(err);
        } catch { detail = `HTTP ${resp.status}`; }
        setStartError(detail);
        setStarting(false);
        return;
      }
      const d = await resp.json();
      if (d.already_audited && d.result) { setResult(d.result); setStatusRow(d.result); setPhase('complete'); }
      else { setStatusRow((prev) => ({ ...prev, status: d.status, progress_percent: 0, current_step: '' })); setPhase('running'); }
    } catch (err) {
      setStartError(err?.message || 'Network error while starting the audit');
    }
    setStarting(false);
  };

  const onReuploadFile = async (e) => {
    const f = e.target.files && e.target.files[0];
    if (e.target) e.target.value = '';
    if (!f) return;
    setReuploading(true);
    setReuploadError('');
    setReuploadMsg('Uploading BEP…');
    try {
      const form = new FormData();
      form.append('bep_file', f);
      const resp = await apiFetch(
        `${API_URL}/api/projects/${projectId}/bep/restore-pdf`,
        { method: 'POST', body: form },
      );
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || `Restore failed (HTTP ${resp.status})`);
      }
      setReuploadMsg('BEP restored. Re-running readiness audit…');
      await startAudit();
      setReuploadMsg('');
    } catch (err) {
      setReuploadError(err.message || 'Re-upload failed');
      setReuploadMsg('');
    } finally {
      setReuploading(false);
    }
  };

  if (phase === 'loading') return null;

  // ── State 1: no audit yet ──
  if (phase === 'none') {
    const noBep = !hasBep && !localBepFile;
    return (
      <Card className="card-elevated" data-testid="readiness-panel-empty">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <ClipboardCheck className="h-4 w-4 text-accent" /> BEP Readiness Audit
          </CardTitle>
          <CardDescription data-testid="readiness-empty-description">
            {noBep
              ? 'Drop a BEP in Step 1 below to enable the readiness audit.'
              : (!hasBep && localBepFile)
                ? `Runs directly on "${localBepFile.name}" — pre-check the BEP before spending an extraction run. Scores readiness criteria and classifies which requirements are machine-checkable. AHA readiness criteria aligned with ISO/openBIM principles.`
                : 'Advisory pre-check of the uploaded BEP: scores readiness criteria and classifies which requirements are machine-checkable before rule extraction. AHA readiness criteria aligned with ISO/openBIM principles.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!readOnly ? <Button
            onClick={startAudit}
            disabled={starting || noBep}
            title={noBep ? 'No BEP uploaded for this project' : undefined}
            data-testid="run-readiness-audit-btn"
          >
            {starting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Starting…</>
              : <><ClipboardCheck className="mr-2 h-4 w-4" /> Run BEP Readiness Audit</>}
          </Button> : (
            <p className="text-sm text-muted-foreground" data-testid="readiness-read-only-empty">
              No stored readiness audit is available for this demo. Starting one is owner-only.
            </p>
          )}
          {startError && (
            <p
              className="mt-2 text-xs text-[hsl(var(--destructive))] whitespace-pre-wrap break-words"
              data-testid="readiness-start-error"
            >
              {startError}
            </p>
          )}
        </CardContent>
      </Card>
    );
  }

  // ── State 2: running ──
  if (phase === 'running') {
    const steps = stepStates(statusRow?.progress_percent, false);
    return (
      <Card className="card-elevated" data-testid="readiness-panel-running">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin text-blue-500" /> BEP Readiness Audit — running
          </CardTitle>
          <CardDescription data-testid="readiness-current-step">
            {statusRow?.current_step || DASH} · {num(statusRow?.progress_percent)}%
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ol className="flex flex-wrap gap-x-4 gap-y-2" data-testid="readiness-stepper">
            {steps.map((s) => (
              <li key={s.label} className="flex items-center gap-1.5 text-xs">
                <span className={`inline-block h-2 w-2 rounded-full ${STEP_DOT[s.state]}`} />
                <span className={s.state === 'pending' ? 'text-muted-foreground' : 'text-foreground'}>{s.label}</span>
              </li>
            ))}
          </ol>
          <div className="mt-3 h-1.5 bg-muted rounded overflow-hidden">
            <div className="h-full bg-blue-500 transition-[width]"
              style={{ width: `${statusRow?.progress_percent || 0}%` }}
              data-testid="readiness-progress-bar" />
          </div>
        </CardContent>
      </Card>
    );
  }

  // ── State 3: failed (collapsible) ──
  if (phase === 'failed') {
    const errStr = String(statusRow?.error_summary || '');
    const pdfMissing = /source pdf no longer on disk|no longer on disk|source pdf/i.test(errStr);
    return (
      <Card className="card-elevated border-[hsl(var(--destructive))]/40" data-testid="readiness-panel-failed">
        <CardHeader>
          <div className="flex items-start gap-1.5">
            <button onClick={() => setCollapsed(!collapsed)} aria-expanded={!collapsed}
              className="mt-0.5 text-muted-foreground hover:text-foreground"
              data-testid="readiness-panel-toggle">
              <ChevronRight className={`h-4 w-4 transition-transform ${collapsed ? '' : 'rotate-90'}`} />
            </button>
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-[hsl(var(--destructive))]" /> BEP Readiness Audit — failed
              </CardTitle>
              <CardDescription className="text-[hsl(var(--destructive))]" data-testid="readiness-error-summary">
                {statusRow?.error_summary || DASH}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        {!collapsed && (
          <CardContent className="space-y-3">
            {pdfMissing && (
              <p className="text-xs text-muted-foreground" data-testid="readiness-pdf-missing-hint">
                The source BEP PDF was purged from disk (inactivity maintenance). Re-upload the
                exact same BEP file to restore it — no re-extraction runs, and the readiness
                audit will re-start automatically.
              </p>
            )}
            {!readOnly && <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf,.pdf"
              className="hidden"
              onChange={onReuploadFile}
              data-testid="reupload-bep-input"
            />}
            {!readOnly && <div className="flex flex-wrap gap-2">
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={reuploading || starting}
                data-testid="reupload-bep-btn"
              >
                {reuploading
                  ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Uploading…</>
                  : <><Upload className="mr-2 h-4 w-4" /> Re-upload BEP</>}
              </Button>
              <Button variant="outline" onClick={startAudit}
                disabled={starting || reuploading} data-testid="rerun-readiness-audit-btn">
                {starting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
                Re-run Readiness Audit
              </Button>
            </div>}
            {reuploadMsg && (
              <p className="text-xs text-muted-foreground" data-testid="reupload-status">
                {reuploadMsg}
              </p>
            )}
            {reuploadError && (
              <p className="text-xs text-[hsl(var(--destructive))]" data-testid="reupload-error">
                {reuploadError}
              </p>
            )}
          </CardContent>
        )}
      </Card>
    );
  }

  // ── State 4: complete (compact summary — the full dashboard lives at
  //     /projects/:id/bep-readiness; this panel shows gauge + blocked
  //     families + a link, nothing else) ──
  const m = result?.metrics || {};
  const cr = result?.checklist_results || {};
  const critCount = result?.critical_gap_count;

  return (
    <Card className="card-elevated" data-testid="readiness-panel-complete">
      <CardHeader>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-1.5">
            <button onClick={() => setCollapsed(!collapsed)} aria-expanded={!collapsed}
              className="mt-1 text-muted-foreground hover:text-foreground"
              data-testid="readiness-panel-toggle">
              <ChevronRight className={`h-4 w-4 transition-transform ${collapsed ? '' : 'rotate-90'}`} />
            </button>
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <CheckCircle2 className={`h-4 w-4 ${critCount > 0 ? 'text-amber-500' : 'text-green-500'}`} />
                BEP Readiness Audit
              </CardTitle>
              {collapsed ? (
                <CardDescription data-testid="readiness-summary-line">
                  Score {num(result?.readiness_score)} · {cr.band || DASH} · {num(critCount)} critical
                  gap{critCount === 1 ? '' : 's'} · {num(m.blocked_requirement_families)} blocked families
                </CardDescription>
              ) : (
                <CardDescription>
                  AHA readiness criteria aligned with ISO/openBIM principles.
                </CardDescription>
              )}
            </div>
          </div>
          {!readOnly && <Button size="sm" variant="outline" onClick={startAudit} disabled={starting}
            title="Re-runs only if the stored audit was reset; a stored result is returned instantly."
            data-testid="readiness-refresh-btn">
            <RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh
          </Button>}
        </div>
      </CardHeader>
      {!collapsed && (
      <CardContent>
        <div className="flex items-center gap-5 flex-wrap">
          <ReadinessGauge score={result?.readiness_score} band={cr.band} compact
            testId="readiness-inline-gauge" />
          <div className="space-y-0.5" data-testid="readiness-inline-blocked">
            <p className="text-[11px] text-muted-foreground">Blocked Families</p>
            <p className={`text-xl font-semibold tabular-nums ${m.blocked_requirement_families > 0 ? 'text-[hsl(var(--destructive))]' : ''}`}
              data-testid="readiness-inline-blocked-count">
              {num(m.blocked_requirement_families)}
            </p>
            <p className="text-[11px] text-muted-foreground">
              {num(m.blocked_source_gaps)} source · {num(m.blocked_engine_gaps)} engine
            </p>
            {m.blocked_requirement_families > 0 && (
              <p className="text-[11px] text-[hsl(var(--destructive))] font-medium">
                BEP remediation required
              </p>
            )}
          </div>
          <div className="ml-auto">
            <Button size="sm"
              onClick={() => navigate(`/projects/${projectId}/bep-readiness`)}
              data-testid="readiness-open-dashboard-btn">
              Open Readiness Dashboard <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
      )}
    </Card>
  );
}
